/**
 * Dark Mode Toggle Component
 *
 * Provides manual dark/light mode switching with localStorage persistence.
 * Respects system preference as default when no manual choice is stored.
 *
 * HOW IT WORKS:
 * 1. On page load, checks localStorage for user preference
 * 2. If no preference, respects system setting (prefers-color-scheme)
 * 3. Toggle button switches between light/dark
 * 4. Choice persists across sessions via localStorage
 *
 * HTML STRUCTURE (add to your theme):
 * <button class="devwp-dark-mode-toggle" aria-label="Toggle dark mode">
 *     <span class="devwp-toggle-icon-light" aria-hidden="true">sun icon</span>
 *     <span class="devwp-toggle-icon-dark" aria-hidden="true">moon icon</span>
 * </button>
 *
 * CSS INTEGRATION:
 * - Sets data-theme="light" or data-theme="dark" on <html>
 * - CSS in color-scheme-output.php handles the actual color switching
 * - :root[data-theme="light"] and :root[data-theme="dark"] selectors
 *
 * ACCESSIBILITY:
 * - Toggle button has aria-label
 * - Icons are aria-hidden
 * - Works with keyboard navigation
 *
 * @since 1.11.0
 */

/**
 * Storage key for theme preference
 * @type {string}
 */
const STORAGE_KEY = 'devwp-theme';

/**
 * Toggle button selector
 * @type {string}
 */
const TOGGLE_SELECTOR = '.devwp-dark-mode-toggle';

/**
 * Module state for cleanup (prevents memory leaks)
 * @type {Object}
 */
const moduleState = {
	mediaQuery: null,
	mediaQueryListener: null,
	initialized: false,
};

/**
 * Log warning in development mode when storage access fails
 *
 * @param {string} operation - The operation that failed (get/set/remove)
 * @param {Error}  error     - The error object
 */
function logStorageWarning( operation, error ) {
	/* eslint-disable no-console */
	if ( window.DEVWP_DEBUG && typeof console !== 'undefined' ) {
		console.warn(
			`DevWP Dark Mode: localStorage ${ operation } failed. ` +
				'Theme preference will not persist. ' +
				'This is normal in private browsing mode.',
			error
		);
	}
	/* eslint-enable no-console */
}

/**
 * Get the user's stored theme preference
 *
 * @return {string|null} 'light', 'dark', or null if no preference stored
 */
function getStoredTheme() {
	try {
		return localStorage.getItem( STORAGE_KEY );
	} catch ( e ) {
		// localStorage might be disabled (private browsing, etc.)
		logStorageWarning( 'get', e );
		return null;
	}
}

/**
 * Store the user's theme preference
 *
 * @param {string} theme - 'light' or 'dark'
 */
function setStoredTheme( theme ) {
	try {
		localStorage.setItem( STORAGE_KEY, theme );
	} catch ( e ) {
		// localStorage might be disabled
		logStorageWarning( 'set', e );
	}
}

/**
 * Clear the stored theme preference (revert to system default)
 */
function clearStoredTheme() {
	try {
		localStorage.removeItem( STORAGE_KEY );
	} catch ( e ) {
		// localStorage might be disabled
		logStorageWarning( 'remove', e );
	}
}

/**
 * Get the system's preferred color scheme
 *
 * @return {string} 'dark' or 'light'
 */
function getSystemPreference() {
	if (
		window.matchMedia &&
		window.matchMedia( '(prefers-color-scheme: dark)' ).matches
	) {
		return 'dark';
	}
	return 'light';
}

/**
 * Get the current effective theme (stored preference or system default)
 *
 * @return {string} 'light' or 'dark'
 */
function getCurrentTheme() {
	const stored = getStoredTheme();
	if ( stored ) {
		return stored;
	}
	return getSystemPreference();
}

/**
 * Apply theme to the document
 *
 * @param {string} theme - 'light' or 'dark'
 */
function applyTheme( theme ) {
	document.documentElement.dataset.theme = theme;
	updateToggleButtons( theme );
}

/**
 * Update all toggle buttons to reflect current theme
 *
 * @param {string} theme - Current theme ('light' or 'dark')
 */
function updateToggleButtons( theme ) {
	const toggles = document.querySelectorAll( TOGGLE_SELECTOR );
	toggles.forEach( ( toggle ) => {
		// Update aria-pressed for accessibility
		toggle.setAttribute(
			'aria-pressed',
			theme === 'dark' ? 'true' : 'false'
		);

		// Update aria-label for screen readers
		const labels = window.devwpDarkMode || {};
		const label =
			theme === 'dark'
				? labels.lightLabel || 'Switch to light mode'
				: labels.darkLabel || 'Switch to dark mode';
		toggle.setAttribute( 'aria-label', label );

		// Toggle active class for styling
		toggle.classList.toggle( 'is-dark', theme === 'dark' );
	} );
}

/**
 * Toggle between light and dark themes
 */
function toggleTheme() {
	const current = getCurrentTheme();
	const newTheme = current === 'dark' ? 'light' : 'dark';

	setStoredTheme( newTheme );
	applyTheme( newTheme );

	// Announce theme change to screen readers
	const announcement = document.getElementById( 'devwp-sr-announcement' );
	if ( announcement ) {
		const labels = window.devwpDarkMode || {};
		announcement.textContent =
			newTheme === 'dark'
				? labels.darkEnabled || 'Dark mode enabled'
				: labels.lightEnabled || 'Light mode enabled';
	}

	// Dispatch custom event for other scripts to react
	document.dispatchEvent(
		new CustomEvent( 'devwp:themechange', {
			detail: { theme: newTheme },
		} )
	);
}

/**
 * Initialize dark mode toggle functionality
 */
function initDarkModeToggle() {
	// Apply initial theme (from storage or system preference)
	const storedTheme = getStoredTheme();
	if ( storedTheme ) {
		// User has explicit preference, apply it
		applyTheme( storedTheme );
	} else {
		// No stored preference - CSS media query handles system preference
		// Just update toggle buttons to reflect system state
		updateToggleButtons( getSystemPreference() );
	}

	// Set up toggle buttons
	const toggles = document.querySelectorAll( TOGGLE_SELECTOR );
	toggles.forEach( ( toggle ) => {
		toggle.addEventListener( 'click', toggleTheme );
	} );

	// Listen for system preference changes (e.g., user changes OS dark mode)
	if ( window.matchMedia ) {
		moduleState.mediaQuery = window.matchMedia(
			'(prefers-color-scheme: dark)'
		);

		// Only respond to system changes if user hasn't set explicit preference
		moduleState.mediaQueryListener = ( e ) => {
			if ( ! getStoredTheme() ) {
				// No stored preference, follow system
				updateToggleButtons( e.matches ? 'dark' : 'light' );
			}
		};

		// Modern browsers
		if ( moduleState.mediaQuery.addEventListener ) {
			moduleState.mediaQuery.addEventListener(
				'change',
				moduleState.mediaQueryListener
			);
		} else if ( moduleState.mediaQuery.addListener ) {
			// Legacy Safari
			moduleState.mediaQuery.addListener(
				moduleState.mediaQueryListener
			);
		}
	}

	moduleState.initialized = true;
}

/**
 * Destroy dark mode toggle functionality
 *
 * Removes event listeners to prevent memory leaks.
 * Call this before re-initializing or when component is unmounted.
 */
function destroyDarkModeToggle() {
	if ( moduleState.mediaQuery && moduleState.mediaQueryListener ) {
		if ( moduleState.mediaQuery.removeEventListener ) {
			moduleState.mediaQuery.removeEventListener(
				'change',
				moduleState.mediaQueryListener
			);
		} else if ( moduleState.mediaQuery.removeListener ) {
			// Legacy Safari
			moduleState.mediaQuery.removeListener(
				moduleState.mediaQueryListener
			);
		}
	}

	const toggles = document.querySelectorAll( TOGGLE_SELECTOR );
	toggles.forEach( ( toggle ) => {
		toggle.removeEventListener( 'click', toggleTheme );
	} );

	// Reset module state
	moduleState.mediaQuery = null;
	moduleState.mediaQueryListener = null;
	moduleState.initialized = false;
}

document.addEventListener( 'DOMContentLoaded', initDarkModeToggle );

// Export for external use
export {
	getCurrentTheme,
	toggleTheme,
	getSystemPreference,
	getStoredTheme,
	setStoredTheme,
	clearStoredTheme,
	destroyDarkModeToggle,
	initDarkModeToggle,
};
